import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useNavigate } from 'react-router-dom'
import { adminService } from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'

/**
 * Platform-wide admin settings. Currently the global email sender (From name +
 * address) used for all outgoing email, unless an event overrides it in its own
 * editor (Edit event → Email sender).
 */
export default function AdminSettingsPage() {
  const { notify } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fromName, setFromName] = useState('')
  const [fromAddress, setFromAddress] = useState('')
  const [allowedDomains, setAllowedDomains] = useState<string[]>([])

  useEffect(() => {
    adminService
      .getEmailSender()
      .then((s) => {
        setFromName(s.fromName)
        setFromAddress(s.fromAddress)
        setAllowedDomains(s.allowedDomains ?? [])
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      const s = await adminService.setEmailSender({ fromName, fromAddress })
      setFromName(s.fromName)
      setFromAddress(s.fromAddress)
      notify('Email sender saved')
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Settings
      </Typography>

      {loading ? (
        <LoadingState />
      ) : (
        <Card variant="outlined" sx={{ p: 3, borderRadius: 3, mt: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Email sender
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The default From name and address for all email the platform sends.
            Individual events can override this in their editor (Edit event →
            Email sender). Leave a field blank to fall back to the deployment
            default.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="From name"
                fullWidth
                placeholder="e.g. Vericast Events"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <TextField
                label="From address"
                fullWidth
                type="email"
                placeholder="e.g. no-reply@vericast.com"
                value={fromAddress}
                onChange={(e) => setFromAddress(e.target.value)}
                helperText={
                  allowedDomains.length
                    ? `Must use an allowed domain: ${allowedDomains.join(', ')}`
                    : undefined
                }
              />
            </Grid>
          </Grid>
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Card>
      )}
    </Container>
  )
}
